import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

const TermCalendar = () => {
  const [activeTerm, setActiveTerm] = useState('autumn');

  const terms = [
    { id: 'autumn', label: 'Autumn Term', icon: '🍂', months: 'September - December' },
    { id: 'spring', label: 'Spring Term', icon: '🌸', months: 'January - April' },
    { id: 'summer', label: 'Summer Term', icon: '☀️', months: 'May - July' }
  ];

  const calendarData = {
    autumn: [
      { date: 'September 5', event: 'First Day of School', type: 'academic' },
      { date: 'September 15', event: 'Parent-Teacher Meeting', type: 'meeting' },
      { date: 'October 10', event: 'Mid-Term Break', type: 'holiday' },
      { date: 'October 25', event: 'Sports Day', type: 'event' },
      { date: 'November 15', event: 'Science Fair', type: 'event' },
      { date: 'December 20', event: 'Winter Concert', type: 'event' },
      { date: 'December 22', event: 'End of Term', type: 'academic' }
    ],
    spring: [
      { date: 'January 8', event: 'First Day of Spring Term', type: 'academic' },
      { date: 'January 20', event: 'Academic Assessment Week', type: 'academic' },
      { date: 'February 14', event: 'Valentine\'s Day Activities', type: 'event' },
      { date: 'March 1', event: 'Spring Break', type: 'holiday' },
      { date: 'March 15', event: 'Art Exhibition', type: 'event' },
      { date: 'April 10', event: 'Easter Activities', type: 'event' },
      { date: 'April 15', event: 'End of Spring Term', type: 'academic' }
    ],
    summer: [
      { date: 'May 2', event: 'First Day of Summer Term', type: 'academic' },
      { date: 'May 20', event: 'Annual Sports Competition', type: 'event' },
      { date: 'June 10', event: 'School Picnic', type: 'event' },
      { date: 'June 25', event: 'Graduation Ceremony', type: 'event' },
      { date: 'July 5', event: 'Summer Camp Begins', type: 'event' },
      { date: 'July 15', event: 'End of Academic Year', type: 'academic' }
    ]
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'academic':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'event':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'meeting':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'holiday':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="term-calendar-page">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden text-white bg-slate-900">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: `url('/src/assets/Tree of Knowledge.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>

        <div className="relative px-4 mx-auto text-center max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <CalendarDaysIcon className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-['Playfair_Display']">
              Term <span className="text-yellow-400">Calendar</span>
            </h1>
            <p className="max-w-4xl mx-auto text-xl leading-relaxed text-slate-300">
              Stay updated with our academic calendar, events, and important dates throughout the year.
              Plan ahead and never miss a special moment in your child's educational journey.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container px-5 mx-auto">
          <div className="max-w-6xl mx-auto">
            {/* Term Selection */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {terms.map((term) => (
                <button
                  key={term.id}
                  className={`flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 font-medium ${activeTerm === term.id
                    ? 'bg-primary-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-primary-100 hover:text-primary-700'
                    }`}
                  onClick={() => setActiveTerm(term.id)}
                >
                  <span className="text-2xl">{term.icon}</span>
                  <div className="text-left">
                    <div className="font-semibold">{term.label}</div>
                    <div className="text-sm opacity-75">{term.months}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Calendar Content */}
            <div className="animate-fade-in">
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* Calendar Events */}
                <div className="card">
                  <h3 className="mb-6 text-2xl font-semibold text-gray-900">
                    {terms.find(t => t.id === activeTerm)?.label} Events
                  </h3>
                  <div className="space-y-4">
                    {calendarData[activeTerm].map((event, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-lg border-l-4 ${getEventTypeColor(event.type)}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold">{event.event}</h4>
                            <p className="mt-1 text-sm opacity-75">{event.date}</p>
                          </div>
                          <span className="px-2 py-1 text-xs capitalize rounded-full bg-white/50">
                            {event.type}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Important Information */}
                <div className="space-y-6">
                  <div className="card">
                    <h3 className="mb-4 text-xl font-semibold text-gray-900">School Hours</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                        <span className="font-medium">Morning Session</span>
                        <span>8:00 AM - 12:00 PM</span>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                        <span className="font-medium">Afternoon Session</span>
                        <span>1:00 PM - 3:30 PM</span>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                        <span className="font-medium">Office Hours</span>
                        <span>7:30 AM - 4:00 PM</span>
                      </div>
                    </div>
                  </div>

                  <div className="card">
                    <h3 className="mb-4 text-xl font-semibold text-gray-900">Contact Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="text-xl">📞</div>
                        <div>
                          <div className="font-medium">Phone</div>
                          <div className="text-gray-600">+1 (555) 123-4567</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-xl">📧</div>
                        <div>
                          <div className="font-medium">Email</div>
                          <div className="text-gray-600">admissions@auressschool.com</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-xl">📍</div>
                        <div>
                          <div className="font-medium">Address</div>
                          <div className="text-gray-600">123 Education Street, City, Country</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Download Calendar */}
              <div className="mt-12 text-center">
                <div className="max-w-2xl mx-auto card">
                  <h3 className="mb-4 text-2xl font-semibold text-gray-900">Download Calendar</h3>
                  <p className="mb-6 text-gray-700">
                    Download our complete academic calendar for offline reference and planning.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <button className="btn btn-primary">
                      📅 Download PDF
                    </button>
                    <button className="btn btn-secondary">
                      📱 Add to Calendar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermCalendar; 